import {UIComponent} from "@webfruits/core";
import {SVGComponent} from "@webfruits/toolbox/dist/components/svg/SVGComponent";
import {gsap} from "gsap";
import {IHotspotIllustrationElementProps} from "./IHotspotIllustrationElementProps";
import {AppConfig} from "../../../config/AppConfig";

/******************************************************************
 * HotspotIllustrationElement
 *
 * @author matthias.schulz@jash.de
 *****************************************************************/

export class HotspotIllustrationElement extends UIComponent {

    /******************************************************************
     * Properties
     *****************************************************************/

    private _htmlImage: UIComponent<HTMLImageElement>;
    private _parameters: IHotspotIllustrationElementProps = {
        x: 0,
        y: 0
    };

    /******************************************************************
     * Constructor
     *****************************************************************/

    constructor(private _svgElement: SVGElement, private _svgRoot: SVGComponent) {
        super("hotspot-illustration-image-element");
        this.initHTMLImage();
        this.initParameters();
    }

    /******************************************************************
     * Public Methodes
     *****************************************************************/

    public playEnterAnimation() {
        gsap.to(this.view, {
            duration: AppConfig.HOTSPOT_HOVER_DURATION,
            x: 0,
            y: 0,
            ease: "power4.out"
        })
    }

    public playLeaveAnimation() {
        gsap.to(this.view, {
            duration: AppConfig.HOTSPOT_HOVER_DURATION,
            x: this._parameters.x,
            y: this._parameters.y,
            ease: "power4.out"
        })
    }

    public updateStyles() {
        this.applyStyle({
            position: "absolute",
            width: this.defaultImageWidth / this._svgRoot.defaultWidth * 100 + "%",
            height: this.defaultImageHeight / this._svgRoot.defaultHeight * 100 + "%",
            left: this.posX / this._svgRoot.defaultWidth * 100 + "%",
            top: this.posY / this._svgRoot.defaultHeight * 100 + "%"
        })
        this._htmlImage.applyStyle({
            position: "absolute",
            width: "100%",
            height: "100%"
        })
    }

    /******************************************************************
     * Private Methodes
     *****************************************************************/

    private initHTMLImage() {
        this._htmlImage = new UIComponent<HTMLImageElement>("img");
        this._htmlImage.view.src = this.svgImage.getAttribute("xlink:href");
        this._htmlImage.view.width = this.defaultImageWidth;
        this._htmlImage.view.height = this.defaultImageHeight;
        this.addChild(this._htmlImage);
    }

    private getParameters(id: string): { [paraName: number]: string } {
        let vars = {};
        id.replace(/[?&]+([^=&]+)=([^&]*)/gi, (substring: string, ...args: any[]) => {
            let paraName = args[0];
            vars[paraName] = args[1];
            return substring;
        });
        return vars;
    }

    private get svgTransform(): string {
        return this._svgElement.getAttribute("transform").split("(")[1].split(")")[0];
    }

    private get posX(): number {
        return parseFloat(this.svgTransform.split(", ")[0]);
    }

    private get posY(): number {
        return parseFloat(this.svgTransform.split(", ")[1]);
    }

    private get svgImage(): SVGImageElement {
        return this._svgElement.getElementsByTagName("image")[0];
    }

    private get defaultImageWidth(): number {
        return parseFloat(this.svgImage.getAttribute("width"));
    }

    private get defaultImageHeight(): number {
        return parseFloat(this.svgImage.getAttribute("height"));
    }

    /******************************************************************
     * Events
     *****************************************************************/

    // no events yet

    private initParameters() {
        const params = this.getParameters(this._svgElement.id) as any;
        this._parameters.x = params.x ?? 0;
        this._parameters.y = params.y ?? 0;
    }
}
